import { useMemo, useRef, useState, type TouchEvent } from 'react';
import { CaretRight, Star } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { UserNameCell } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui-kit/sheet';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { cn } from '@/lib/utils';
import { dateOnlyOf, formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import {
  LeaveCalendarEmployeeGroup,
  LeaveCalendarHolidayEvent,
  LeaveCalendarLeaveEvent,
} from '../../types/leave-calendar.types';
import {
  coversDay,
  formatDayLabel,
  weekDaysFrom,
  weekStartOf,
  weekdayIndexOf,
} from '../../utils/leave-calendar-week';

interface LeaveCalendarMobileProps {
  /** The days of the period shown ("YYYY-MM-DD"): a week as a strip, a month as a grid. */
  days: string[];
  /** The day whose leave is listed; one of `days`. */
  selectedDay: string;
  /** Today in the organization's time zone, ringed when it is one of `days`. */
  today: string;
  /** Everyone on leave in the period, already narrowed by the search. */
  employees: LeaveCalendarEmployeeGroup[];
  holidays: LeaveCalendarHolidayEvent[];
  isLoading: boolean;
  onSelectDay: (day: string) => void;
  /** A sideways swipe over the days: 1 for the next period, -1 for the previous one. */
  onStep: (direction: 1 | -1) => void;
}

interface DayEntry {
  employee: LeaveCalendarEmployeeGroup;
  /** The employee's leaves covering the day (two half days can share one). */
  leaves: LeaveCalendarLeaveEvent[];
}

const LEAVE_TYPE_KEYS: Record<string, string> = { FullDay: 'FULL_DAY', HalfDay: 'HALF_DAY' };

/** More days than a week are drawn as a month grid. */
const WEEK_LENGTH = 7;
/** Dots under a day, one per employee on leave up to this many. */
const MAX_DOTS = 3;
/** Cards listed before "Show more". */
const LIST_PAGE = 20;
/** Horizontal travel (px) a touch needs before it counts as a swipe. */
const SWIPE_DISTANCE = 50;

const leaveName = (leave: LeaveCalendarLeaveEvent) => leave.LeaveCategory || leave.Title;

const leaveRange = (leave: LeaveCalendarLeaveEvent) => {
  const start = formatDateOnly(leave.StartDate);
  const end = formatDateOnly(leave.EndDate);
  return !end || dateOnlyOf(leave.StartDate) === dateOnlyOf(leave.EndDate)
    ? start
    : `${start} - ${end}`;
};

/** Calls `onStep` on a mostly-horizontal swipe, so scrolling the page up and down never pages. */
const useSwipe = (onStep: (direction: 1 | -1) => void) => {
  const start = useRef<{ x: number; y: number } | null>(null);
  return {
    onTouchStart: (event: TouchEvent) => {
      const touch = event.touches[0];
      start.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
    },
    onTouchEnd: (event: TouchEvent) => {
      const touch = event.changedTouches[0];
      const from = start.current;
      start.current = null;
      if (!from || !touch) return;
      const dx = touch.clientX - from.x;
      const dy = touch.clientY - from.y;
      if (Math.abs(dx) >= SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy) * 1.5) {
        onStep(dx < 0 ? 1 : -1);
      }
    },
  };
};

const DayButton = ({
  day,
  label,
  weekday,
  count,
  holidayNames,
  isToday,
  isSelected,
  isLoading,
  onSelect,
}: {
  day: string;
  label: string;
  weekday?: string;
  count: number;
  holidayNames: string[];
  isToday: boolean;
  isSelected: boolean;
  isLoading: boolean;
  onSelect: (day: string) => void;
}) => {
  const { t, i18n } = useTranslation();
  const fullDate = formatDayLabel(day, i18n.language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const ariaLabel = [
    fullDate,
    !isLoading && count > 0 && t('LEAVE_CALENDAR_ON_LEAVE_COUNT', { count }),
    ...holidayNames,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <button
      type="button"
      onClick={() => onSelect(day)}
      aria-label={ariaLabel}
      aria-pressed={isSelected}
      className={cn(
        'flex min-h-14 min-w-0 flex-col items-center justify-start gap-0.5 rounded-lg py-1.5 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        holidayNames.length > 0 && !isSelected && 'bg-muted',
        isToday && !isSelected && 'ring-1 ring-inset ring-primary',
        isSelected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
      )}
    >
      {weekday && (
        <span
          className={cn(
            'text-[11px] leading-none',
            isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'
          )}
        >
          {weekday}
        </span>
      )}
      <span
        className={cn(
          'text-sm font-medium leading-5',
          isToday && !isSelected && 'text-primary'
        )}
      >
        {label}
      </span>
      <span className="flex h-2 items-center gap-0.5" aria-hidden>
        {holidayNames.length > 0 && (
          <Star
            weight="fill"
            className={cn('h-2 w-2', isSelected ? 'text-primary-foreground' : 'text-muted-foreground')}
          />
        )}
        {!isLoading &&
          Array.from({ length: Math.min(count, MAX_DOTS) }, (_, index) => (
            <span
              key={index}
              className={cn('h-1 w-1 rounded-full', isSelected ? 'bg-primary-foreground' : 'bg-primary')}
            />
          ))}
      </span>
    </button>
  );
};

const DayCard = ({ entry, onOpen }: { entry: DayEntry; onOpen: () => void }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-2 rounded-md border border-l-4 border-l-primary bg-background px-3 py-2 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="min-w-0 flex-1 space-y-1">
        <UserNameCell name={entry.employee.DisplayName} />
        {entry.leaves.map((leave) => {
          const typeKey = leave.LeaveType ? LEAVE_TYPE_KEYS[leave.LeaveType] : undefined;
          return (
            <div key={leave.ItemId} className="min-w-0 text-xs">
              <p className="truncate font-medium text-accent-foreground">
                {leaveName(leave)}
                {typeKey && (
                  <span className="font-normal text-muted-foreground"> · {t(typeKey)}</span>
                )}
              </p>
              <p className="truncate text-muted-foreground">{leaveRange(leave)}</p>
            </div>
          );
        })}
      </div>
      <CaretRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
    </button>
  );
};

/** The opened employee's leaves on the day, in a bottom sheet (the desktop grid's tooltip). */
const LeaveDetailSheet = ({ entry, onClose }: { entry?: DayEntry; onClose: () => void }) => {
  const { t } = useTranslation();
  // Looked up only for the opened employee: the calendar's own query carries no emails.
  const { byUserId } = useEmployeesByUserIds(entry ? [entry.employee.ItemId] : []);
  const email = entry && (entry.employee.Email ?? byUserId.get(entry.employee.ItemId)?.Email);

  return (
    <Sheet open={!!entry} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-xl">
        {entry && (
          <>
            <SheetHeader className="pr-8 text-left">
              <SheetTitle className="truncate">{entry.employee.DisplayName}</SheetTitle>
              <SheetDescription className="truncate">
                {email || t('LEAVE_CALENDAR_LEAVE_DETAILS')}
              </SheetDescription>
            </SheetHeader>
            <dl className="mt-4 space-y-3">
              {entry.leaves.map((leave) => {
                const typeKey = leave.LeaveType ? LEAVE_TYPE_KEYS[leave.LeaveType] : undefined;
                return (
                  <div
                    key={leave.ItemId}
                    className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md border border-l-4 border-l-primary p-3 text-sm"
                  >
                    <dt className="text-muted-foreground">{t('LEAVE_CATEGORY')}</dt>
                    <dd className="min-w-0 break-words font-medium">{leaveName(leave)}</dd>
                    <dt className="text-muted-foreground">{t('DATE_RANGE')}</dt>
                    <dd>{leaveRange(leave)}</dd>
                    {typeKey && (
                      <>
                        <dt className="text-muted-foreground">{t('LEAVE_TYPE')}</dt>
                        <dd>{t(typeKey)}</dd>
                      </>
                    )}
                  </div>
                );
              })}
            </dl>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

/**
 * Leave on a phone: the period's days as a week strip or a month grid, each marked with a dot per
 * employee on leave (up to three) and a star on a holiday, and below them the people on leave on the
 * picked day. A card opens that employee's leave in a bottom sheet; swiping the days pages the period.
 */
export const LeaveCalendarMobile = ({
  days,
  selectedDay,
  today,
  employees,
  holidays,
  isLoading,
  onSelectDay,
  onStep,
}: LeaveCalendarMobileProps) => {
  const { t, i18n } = useTranslation();
  const swipe = useSwipe(onStep);
  const isMonth = days.length > WEEK_LENGTH;
  const [openUserId, setOpenUserId] = useState<string>();
  const [listed, setListed] = useState({ day: selectedDay, count: LIST_PAGE });
  // Back to the first page of cards whenever another day is picked.
  const listCount = listed.day === selectedDay ? listed.count : LIST_PAGE;

  // Distinct employees on leave per day; an employee with two leaves on one day counts once.
  const countByDay = useMemo(() => {
    const counts = new Map<string, number>();
    for (const day of days) {
      counts.set(
        day,
        employees.filter((employee) =>
          employee.LeaveEvents.some((leave) => coversDay(leave.StartDate, leave.EndDate, day))
        ).length
      );
    }
    return counts;
  }, [days, employees]);

  const holidayNamesOn = useMemo(() => {
    const byDay = new Map<string, string[]>();
    for (const holiday of holidays) {
      const day = dateOnlyOf(holiday.StartDate);
      byDay.set(day, [...(byDay.get(day) ?? []), holiday.Name]);
    }
    return (day: string) => byDay.get(day) ?? [];
  }, [holidays]);

  const entries = useMemo<DayEntry[]>(
    () =>
      employees.flatMap((employee) => {
        const leaves = employee.LeaveEvents.filter((leave) =>
          coversDay(leave.StartDate, leave.EndDate, selectedDay)
        );
        return leaves.length > 0 ? [{ employee, leaves }] : [];
      }),
    [employees, selectedDay]
  );
  // Closes by itself when the opened employee drops off the day (another day, search, refetch).
  const openEntry = entries.find((entry) => entry.employee.ItemId === openUserId);

  const weekdayLabels = weekDaysFrom(weekStartOf(days[0])).map((day) =>
    formatDayLabel(day, i18n.language, { weekday: 'narrow' })
  );
  const selectedHolidays = holidayNamesOn(selectedDay);
  const selectedLabel = formatDayLabel(selectedDay, i18n.language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const dayButton = (day: string, withWeekday: boolean) => (
    <DayButton
      key={day}
      day={day}
      weekday={withWeekday ? formatDayLabel(day, i18n.language, { weekday: 'narrow' }) : undefined}
      label={formatDayLabel(day, i18n.language, { day: 'numeric' })}
      count={countByDay.get(day) ?? 0}
      holidayNames={holidayNamesOn(day)}
      isToday={day === today}
      isSelected={day === selectedDay}
      isLoading={isLoading}
      onSelect={onSelectDay}
    />
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="touch-pan-y rounded-md border p-1.5" {...swipe}>
        {isMonth ? (
          <>
            <div className="grid grid-cols-7 pb-1" aria-hidden>
              {weekdayLabels.map((label, index) => (
                <span key={index} className="text-center text-[11px] text-muted-foreground">
                  {label}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {/* Weeks start on Monday; blank cells push the 1st under its weekday. */}
              {Array.from({ length: weekdayIndexOf(days[0]) }, (_, index) => (
                <span key={`blank-${index}`} aria-hidden />
              ))}
              {days.map((day) => dayButton(day, false))}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-7 gap-0.5">{days.map((day) => dayButton(day, true))}</div>
        )}
      </div>

      <div className="flex items-baseline justify-between gap-2">
        <h2 className="min-w-0 truncate text-sm font-semibold">{selectedLabel}</h2>
        {!isLoading && entries.length > 0 && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {t('LEAVE_CALENDAR_ON_LEAVE_COUNT', { count: entries.length })}
          </span>
        )}
      </div>

      {selectedHolidays.map((name, index) => (
        <div
          key={index}
          className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground"
        >
          <Star weight="fill" className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">
            {t('HOLIDAY')}: {name}
          </span>
        </div>
      ))}

      {isLoading ? (
        Array.from({ length: 3 }, (_, index) => <Skeleton key={index} className="h-16 w-full" />)
      ) : entries.length === 0 ? (
        <p className="rounded-md border py-8 text-center text-sm text-muted-foreground">
          {t('LEAVE_CALENDAR_EMPTY_DAY', { date: selectedLabel })}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {entries.slice(0, listCount).map((entry) => (
            <li key={entry.employee.ItemId}>
              <DayCard entry={entry} onOpen={() => setOpenUserId(entry.employee.ItemId)} />
            </li>
          ))}
        </ul>
      )}

      {!isLoading && entries.length > listCount && (
        <Button
          variant="outline"
          onClick={() => setListed({ day: selectedDay, count: listCount + LIST_PAGE })}
        >
          {t('SHOW_MORE')}
        </Button>
      )}

      <LeaveDetailSheet entry={openEntry} onClose={() => setOpenUserId(undefined)} />
    </div>
  );
};
